import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    Month,
    Recurrence,
    ControlsPageFiltersForControlsMonthlyPageSpecificFilters as ControlsMonthlyFilters,
    ControlsMonthlyPageSpecificFilters,
    ControlsMonthlyColumn,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsMonthlyColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    status: null,
    recurrenceMonthly: null,
    notification: null,
    scheduleMonthly: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    controlResultCount: null,
    nextDueDate: null,
};

const ALL_COLUMNS: ControlsMonthlyColumn[] = Object.keys(
    allColumnsGuard
) as ControlsMonthlyColumn[];
const encodeControlsMonthlyColumn: (col: string) => ControlsMonthlyColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsMonthlySpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsMonthlyPageSpecificFilters {
    return {
        notification: encodeOrFilter<number>("notification", filters),
        recurrenceMonthly: encodeOrFilter<Recurrence>(
            "recurrenceMonthly",
            filters
        ),
        scheduleMonthly: encodeOrFilter<Month[]>("scheduleMonthly", filters),
    };
}
const encodeControlsMonthlyPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsMonthlyFilters = createEncodePageFiltersFn(
    encodeControlsMonthlySpecificPageFilters
);

function decodeControlsMonthlyPageSpecificFilters(
    specificFilters: ControlsMonthlyPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.notification) {
        output.push(
            decodeOrFilter<number>("notification", specificFilters.notification)
        );
    }
    if (specificFilters.recurrenceMonthly) {
        output.push(
            decodeOrFilter<Recurrence>(
                "recurrenceMonthly",
                specificFilters.recurrenceMonthly
            )
        );
    }
    if (specificFilters.scheduleMonthly) {
        output.push(
            decodeOrFilter<Month[]>(
                "scheduleMonthly",
                specificFilters.scheduleMonthly
            )
        );
    }
    return output;
}
const decodeControlsMonthlyPageFilters: (
    pageFilters: ControlsMonthlyFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsMonthlyPageSpecificFilters
);

// Page state
//
type ControlsMonthlyPageStateWithType = PageStateData<"ControlsMonthly"> & {
    type: "ControlsMonthly";
};

function decodeControlsMonthlyPageState(
    pageState: ControlsMonthlyPageStateWithType
): PageFilterAndColumns {
    const monthlylPageState = unwrapPageStateAs(pageState, "ControlsMonthly");
    const filter: SimpleAndFilter = {
        filters: decodeControlsMonthlyPageFilters(monthlylPageState.filters),
    };
    const visibleColumnIds = new Set(monthlylPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlsMonthlyPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsMonthlyPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsMonthly",
        filters: encodeControlsMonthlyPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(encodeControlsMonthlyColumn),
        version: filterAndColumns.version,
    };
}

export { encodeControlsMonthlyPageState, decodeControlsMonthlyPageState };

export type { ControlsMonthlyPageStateWithType };
